import React, { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useQuery } from 'hooks/apollo';
import { gql } from '@apollo/client';
import { Button } from 'antd';
import { EditFilled } from '@ant-design/icons';

import Table from 'components/Table';
import SettingsDrawer from 'components/Settings/SettingsDrawer';

import { LICENSE_SETTINGS } from 'shared/constants/settings';
import globalMessages from 'translations/messages';
import LanguageTag from 'components/LanguageTag';
import useUser from 'hooks/useUser';
import { renderTruthy } from 'components/Table/renderers';
import DataSourceTypeLabel from 'components/DataSourceTypeLabel';
import { useHistory } from 'react-router';
import PrivacySettingLabel from 'components/PrivacySettingLabel';
import messages from './messages';

const websiteFragment = gql`
  fragment websiteFragment on Website {
    id
    url
    additionalUrls
    excludedUrls
    language
    languageName
    additionalLanguages {
      id
      language
      languageName
      url
    }
    trackVisitors
    useCookies
    customCSS
    script {
      id
      key
    }
  }
`;

const dataSourceFragment = gql`
  fragment dataSourceFragment on DataSource {
    id
    title
    type
    website {
      ...websiteFragment
    }
    access {
      id
      roles {
        id
        permissions
      }
    }
    activeLicense {
      id
      license {
        id
        title
        uniqueTitle
      }
      startDate
      endDate
      settings(keys: ["userLimit", "advancedUserLimit"])
    }
    licenseSettings
  }
`;

const QUERY = gql`
  query EditCustomer_GetWebsites($id: ID!) {
    customer (id: $id) {
      id
      dataSources {
        ...dataSourceFragment
      }
      scripts {
        id
        key
      }
    }
    settings:settingsArray(keys: ["dataSourceAdvancedUserRoleId"]) {
      id
      key
      value
    }
  }
  ${websiteFragment}
  ${dataSourceFragment}
`;

function renderUsers(dataSourceAccess, dataSource) {
  const users = dataSourceAccess.length;
  if (dataSource.activeLicense?.settings) {
    const { userLimit } = dataSource.activeLicense.settings;
    if (userLimit > 0) {
      return `${users} / ${userLimit}`;
    }
  }

  return users;
}

function renderAdvancedUsers(dataSourceAccess, dataSource, dataSourceAdvancedUserRoleId) {
  const advancedUsers = dataSourceAccess.filter(({ roles }) => roles.find(r => r.id === dataSourceAdvancedUserRoleId)).length;
  if (dataSource.activeLicense?.settings) {
    const { advancedUserLimit } = dataSource.activeLicense.settings;
    if (advancedUserLimit > 0) {
      return `${advancedUsers} / ${advancedUserLimit}`;
    }
  }

  return advancedUsers;
}

const CustomerDataSources = ({ customer }) => {
  const [editLicenseSettings, setEditLicenseSettings] = useState();
  const { data, loading, refetch } = useQuery(QUERY, {
    variables: {
      id: customer.id,
    },
  });
  const { isSuperUser, changeCustomer, hasDataSourceAccess } = useUser();
  const history = useHistory();

  const dataSourceAdvancedUserRoleId = data?.settings.find(s => s.key === 'dataSourceAdvancedUserRoleId')?.value;

  const columns = [
    {
      dataIndex: 'title',
      title: <FormattedMessage {...globalMessages.title} />,
      defaultSortOrder: 'ascend',
    },
    {
      dataIndex: 'type',
      title: <FormattedMessage {...globalMessages.type} />,
      render: (type) => <DataSourceTypeLabel type={type} />,
    },
    {
      dataIndex: ['website', 'language'],
      title: <FormattedMessage {...globalMessages.languages} />,
      render: (language, { website }) => website && (
        <>
          <LanguageTag {...website} />
          {website.additionalLanguages.map(l => (
            <LanguageTag key={l.language} {...l} />
          ))}
        </>
      ),
    },
    {
      dataIndex: 'licenseSettings',
      title: 'License overrides',
      render: (licenseSettings, { id }) => (
        <>
          {Object.keys(licenseSettings).length}
          {isSuperUser && <Button onClick={() => setEditLicenseSettings(id)} icon={<EditFilled />} style={{ marginLeft: '1em' }} />}
        </>
      ),
      align: 'right',
      visible: isSuperUser,
    },
    {
      key: 'users',
      dataIndex: 'access',
      title: <FormattedMessage {...globalMessages.users} />,
      render: renderUsers,
      align: 'right',
    },
    {
      key: 'advancedUsers',
      dataIndex: 'access',
      title: <FormattedMessage {...messages.advancedUsers} />,
      render: (access, dataSource) => renderAdvancedUsers(access, dataSource, dataSourceAdvancedUserRoleId),
      align: 'right',
    },
    {
      dataIndex: ['activeLicense'],
      title: 'Active license',
      type: 'bool',
      render: renderTruthy,
    },
    {
      dataIndex: ['website', 'trackVisitors'],
      title: 'Tracking',
      render: (trackVisitors) => <PrivacySettingLabel value={trackVisitors} />,
    },
    {
      dataIndex: ['website', 'useCookies'],
      title: 'Cookies',
      render: (useCookies) => <PrivacySettingLabel value={useCookies} />,
    },
  ];

  const onCloseEditLicenseDrawer = () => {
    setEditLicenseSettings(false);
    refetch();
  };

  const onView = async (record) => {
    await changeCustomer(customer.id);
    history.push(`/data-sources/${record.id}`);
  };

  const actions = (record) => hasDataSourceAccess(record.id) && (
    <Button onClick={() => onView(record)}>
      View
    </Button>
  );

  return (
    <>
      <Table
        loading={loading}
        columns={columns}
        dataSource={data?.customer.dataSources}
        actions={actions}
      />
      <SettingsDrawer open={!!editLicenseSettings} relationId={editLicenseSettings} settings={LICENSE_SETTINGS} onDone={onCloseEditLicenseDrawer} onCancel={onCloseEditLicenseDrawer} edit />
    </>
  );
};

export default CustomerDataSources;
